<template>
  <div class="picker-page">

    <!-- Header -->
    <div class="picker-header">
      <div>
        <Pill style="margin-bottom:12px;">Paso 1 · Comedores</Pill>
        <h1 class="picker-title">¿Para qué comedores vas a pedir hoy?</h1>
        <p class="picker-subtitle">
          Selecciona uno o varios — podrás ver y editar las cantidades de cada comedor en la siguiente pantalla.
        </p>
      </div>
      <div class="picker-header__actions">
        <button @click="markAll" class="link-btn">Marcar todos</button>
        <button @click="clearAll" class="link-btn">Limpiar</button>
      </div>
    </div>

    <!-- Steps -->
    <div class="picker-steps">
      <Steps step="cliente" />
    </div>

    <!-- Empty state -->
    <div v-if="centers.length === 0" class="picker-empty">
      No hay comedores disponibles.
    </div>

    <!-- Grid 3 columnas -->
    <div v-else class="centers-grid">
      <button
        v-for="c in centers"
        :key="c.id"
        @click="toggle(c.id)"
        class="center-card"
        :class="{ 'center-card--active': isActive(c.id) }"
      >
        <!-- Top row: checkbox + last date -->
        <div class="center-card__top">
          <div class="center-checkbox" :class="{ 'center-checkbox--active': isActive(c.id) }">
            <Icon v-if="isActive(c.id)" name="check" :size="13" color="#fff" :stroke="3" />
          </div>
          <div class="center-card__date">
            <Icon name="clock" :size="12" />
            {{ lastOrders[c.id]?.date || '—' }}
          </div>
        </div>

        <!-- Name + address -->
        <div class="center-card__name">{{ c.name }}</div>
        <div class="center-card__address">
          <div>{{ c.address }}</div>
          <div v-if="c.ruta || c.cadena" class="center-card__route">
            <span v-if="c.ruta">Ruta {{ c.ruta }}</span>
            <span v-if="c.ruta && c.cadena"> · </span>
            <span v-if="c.cadena">{{ c.cadena }}</span>
          </div>
        </div>

        <!-- Footer: last order -->
        <div class="center-card__footer">
          <span>Último pedido</span>
          <span class="center-card__order-id">
            {{ lastOrders[c.id]?.orderId ? '#' + lastOrders[c.id].orderId : '—' }}
          </span>
        </div>
      </button>
    </div>

    <!-- Footer sticky -->
    <div class="picker-footer">
      <div class="picker-footer__count">
        <span class="picker-footer__count-num">{{ activeIds.length }}</span>
        {{ activeIds.length === 1 ? 'comedor seleccionado' : 'comedores seleccionados' }}
      </div>
      <button
        @click="handleContinue"
        :disabled="activeIds.length === 0"
        class="btn-continue"
        :class="{ 'btn-continue--disabled': activeIds.length === 0 }"
      >
        <Icon name="chev" :size="16" color="#fff" />
        Continuar a productos
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Pill  from './ui/Pill.vue'
import Icon  from './ui/Icon.vue'
import Steps from './ui/Steps.vue'

const props = defineProps({
  activeIds: { type: Array,  default: () => [] },
  store:     { type: Object, required: true },
})

const emit = defineEmits(['update:activeIds', 'continue'])

const centers    = computed(() => props.store.centers.value)
const lastOrders = computed(() => props.store.lastOrders)

function isActive(id) { return props.activeIds.includes(id) }

function toggle(id) {
  const next = isActive(id)
    ? props.activeIds.filter(x => x !== id)
    : [...props.activeIds, id]
  emit('update:activeIds', next)
}

function markAll() { emit('update:activeIds', centers.value.map(c => c.id)) }
function clearAll() { emit('update:activeIds', []) }

function handleContinue() {
  if (props.activeIds.length > 0) emit('continue')
}
</script>

<style scoped>
/* ─── Page wrapper ───────────────────────────────────────────────────────────── */
.picker-page {
  padding: 36px 48px 48px;
  max-width: 1180px;
  margin: 0 auto;
}

/* ─── Header ─────────────────────────────────────────────────────────────────── */
.picker-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 8px;
}

.picker-title {
  margin: 0;
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 28px;
  color: var(--text);
  letter-spacing: -0.3px;
}

.picker-subtitle {
  margin: 8px 0 0;
  font-family: var(--ff-body);
  font-size: 15px;
  color: var(--text-2);
  max-width: 580px;
}

.picker-header__actions {
  display: flex;
  gap: 8px;
}

.link-btn {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 13px;
  color: var(--text-2);
  border-radius: 6px;
}

/* ─── Steps row ─────────────────────────────────────────────────────────────── */
.picker-steps {
  margin-top: 32px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}

/* ─── Empty state ────────────────────────────────────────────────────────────── */
.picker-empty {
  margin-top: 40px;
  text-align: center;
  color: var(--text-2);
  font-family: var(--ff-body);
  font-size: 14px;
}

/* ─── Centers grid ───────────────────────────────────────────────────────────── */
.centers-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 24px;
}

/* ─── Center card ────────────────────────────────────────────────────────────── */
.center-card {
  text-align: left;
  cursor: pointer;
  padding: 20px;
  background: var(--surface);
  border: 1.5px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow);
  transition: all 0.15s;
}

.center-card--active {
  border-color: var(--primary);
  box-shadow: 0 0 0 4px var(--primary-soft);
}

.center-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}

.center-card__date {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--ff-body);
  color: var(--text-3);
  font-size: 11px;
}

/* ─── Checkbox ───────────────────────────────────────────────────────────────── */
.center-checkbox {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  border: 2px solid var(--border-strong);
  background: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s, background 0.15s;
}

.center-checkbox--active {
  border-color: var(--primary);
  background: var(--primary);
}

/* ─── Card content ───────────────────────────────────────────────────────────── */
.center-card__name {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 18px;
  color: var(--text);
  letter-spacing: -0.2px;
}

.center-card__address {
  font-family: var(--ff-body);
  font-size: 12px;
  color: var(--text-2);
  margin-top: 4px;
  margin-bottom: 14px;
  line-height: 1.4;
}

.center-card__route {
  margin-top: 2px;
}

.center-card__footer {
  padding-top: 12px;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: var(--ff-body);
  font-size: 12px;
  color: var(--text-2);
}

.center-card__order-id {
  color: var(--text);
  font-weight: 700;
}

/* ─── Footer CTA ─────────────────────────────────────────────────────────────── */
.picker-footer {
  margin-top: 28px;
  padding: 16px 20px;
  background: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  bottom: 24px;
  box-shadow: var(--shadow-lg);
}

.picker-footer__count {
  font-family: var(--ff-body);
  font-size: 14px;
  color: var(--text-2);
}

.picker-footer__count-num {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 20px;
  color: var(--text);
  margin-right: 6px;
}

.btn-continue {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 52px;
  padding: 0 22px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 15px;
  white-space: nowrap;
  transition: opacity 0.15s;
}

.btn-continue--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
